import React, { useState } from 'react';
import { BudgetProvider, useBudget } from './BudgetContext';
import { TransactionList } from './TransactionList';
import { FilterBar } from './FilterBar';
import { BulkActionBar } from './BulkActionBar';
import { ImportTransactionsModal } from './ImportTransactionsModal';
import { CategoryManager } from './CategoryManager';
import { AccountManager } from './AccountManager';
import { BudgetAnalyticsPanel } from './analytics/BudgetAnalyticsPanel';
import { BudgetSwitcher } from './BudgetSwitcher';
import { Button } from '../ui/Button';
import { FaPlus, FaFileImport, FaTags, FaUniversity, FaCheckSquare, FaTimes, FaLock } from 'react-icons/fa';
import styles from './BudgetPage.module.css';
import './BudgetVariables.css';

const BudgetPageContent: React.FC = () => {
  const {
    selectedIds,
    setIsCreating,
    isSelectMode,
    enterSelectMode,
    exitSelectMode,
    selectAll,
    clearSelection,
    filteredTransactions,
    isReadOnly,
    viewingUserName,
    sharedBudgets,
    analyticsAppliedFilter,
    clearAnalyticsAppliedFilter,
  } = useBudget();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showAccountManager, setShowAccountManager] = useState(false);

  const handleAddTransaction = () => {
    setIsCreating(true);
  };

  const hasSelectedItems = selectedIds.size > 0;
  const hasTransactions = filteredTransactions.length > 0;
  const allSelected = hasTransactions && selectedIds.size === filteredTransactions.length;
  const hasSharedBudgets = sharedBudgets.length > 0;

  return (
    <div className={styles.budgetPage}>
      {/* Read-only banner when viewing shared budget */}
      {isReadOnly && viewingUserName && (
        <div className={styles.readOnlyBanner}>
          {FaLock({ className: styles.readOnlyIcon })}
          <span>Viewing {viewingUserName}'s budget (read-only)</span>
        </div>
      )}

      <header className={styles.header}>
        <div className={`${styles.titleBlock} mobile-page-title-anchor`}>
          <h1 className={styles.title}>Budget</h1>
          <p className={styles.subtitle}>Track transactions, accounts, and categories across your budgets.</p>
        </div>
        {hasSharedBudgets && <BudgetSwitcher />}
      </header>

      <BudgetAnalyticsPanel />

      <section className={styles.section} id="budget-transactions" aria-label="Transactions">
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleRow}>
            <h2 className={styles.sectionTitle}>Transactions</h2>
            {analyticsAppliedFilter && (
              <div className={styles.sectionMeta} aria-label="Filtered from analytics">
                <span className={styles.metaPill}>
                  Filtered from Analytics: {analyticsAppliedFilter.categoryName} • {analyticsAppliedFilter.year}-
                  {String(analyticsAppliedFilter.monthIndex + 1).padStart(2, '0')}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={FaTimes({})}
                  onClick={clearAnalyticsAppliedFilter}
                  aria-label="Clear analytics-applied filter indicator"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {!isReadOnly && (
            <div className={styles.sectionActions}>
              {isSelectMode ? (
                <>
                  <Button variant="ghost" size="sm" onClick={allSelected ? clearSelection : selectAll}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button variant="ghost" size="sm" leftIcon={FaTimes({})} onClick={exitSelectMode}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {hasTransactions && (
                    <Button variant="ghost" size="sm" leftIcon={FaCheckSquare({})} onClick={enterSelectMode}>
                      Select
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={FaUniversity({})}
                    onClick={() => setShowAccountManager(true)}
                  >
                    Accounts
                  </Button>
                  <Button variant="ghost" size="sm" leftIcon={FaTags({})} onClick={() => setShowCategoryManager(true)}>
                    Categories
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={FaFileImport({})}
                    onClick={() => setShowImportModal(true)}
                  >
                    Import
                  </Button>
                  <Button variant="primary" size="sm" leftIcon={FaPlus({})} onClick={handleAddTransaction}>
                    Add
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className={styles.sectionBody}>
          <FilterBar />
          {hasSelectedItems && <BulkActionBar />}
          <TransactionList />
        </div>
      </section>

      {showImportModal && (
        <ImportTransactionsModal onClose={() => setShowImportModal(false)} />
      )}

      {showCategoryManager && (
        <CategoryManager onClose={() => setShowCategoryManager(false)} />
      )}

      {showAccountManager && (
        <AccountManager onClose={() => setShowAccountManager(false)} />
      )}
    </div>
  );
};

export const BudgetPage: React.FC = () => {
  return (
    <BudgetProvider>
      <BudgetPageContent />
    </BudgetProvider>
  );
};

export default BudgetPage;
